import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { SoftwareUpdateService } from '../services/software-update.service';
import { SoftwareUpdate } from '../models/software-update.model';
import { ToastService } from '../../../shared/services/toast-service';
import { BreadcrumbService } from '../../../shared/components/breadcrumbs/breadcrumb.service';
import { AssignDevicesTabComponent } from './tabs/assign-devices-tab.component';
import { RolloutTabComponent } from './tabs/rollout-tab.component';
import { LabelBadge } from '../../../shared/components/label-badge/label-badge';

type DetailTab = 'assign' | 'rollout';

/**
 * Software update detail page — header shared by both tabs, plus the Assign
 * devices / Rollout tab strip described in the design handoff ("Assign
 * Software Update - Wireframes", turn 4). Routed at
 * `admin/software-updates/:id` (not under `:organizationName` — this is a
 * cross-tenant, super-admin-only screen; tenant access control is out of
 * scope per the handoff).
 *
 * The breadcrumb trail itself is NOT rendered here — `<app-breadcrumbs>` in
 * the app shell derives it from route `data.breadcrumb` config (see
 * app.routes.ts and BreadcrumbService), same pattern as the tenant/device
 * drill-downs. This component only names the `:id` segment once the update
 * loads, so the trail shows its name/version instead of the raw uuid.
 */
@Component({
  selector: 'app-software-update-detail',
  standalone: true,
  imports: [DatePipe, AssignDevicesTabComponent, RolloutTabComponent, LabelBadge],
  templateUrl: './software-update-detail.component.html',
  // `display: contents` so the plain <app-software-update-detail> host (an
  // unstyled custom element — Angular doesn't register it as a real element
  // with a default display) doesn't sit in the box tree between <main> and
  // this component's flex-column root. Without it, the root's min-h-full
  // can't resolve against <main>'s height, so the footer never reaches the
  // page bottom — see the sticky-footer fix in assign-devices-tab.
  host: { class: 'contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoftwareUpdateDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(SoftwareUpdateService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breadcrumbs = inject(BreadcrumbService);

  readonly update = signal<SoftwareUpdate | null>(null);
  readonly isLoading = signal(false);
  readonly activeTab = signal<DetailTab>('assign');

  /** Count of devices currently assigned this update, shown as the Rollout
   *  tab badge — fetched separately from the full RolloutTabComponent so the
   *  badge is correct even while that tab isn't the active one. */
  readonly assignedCount = signal<number | null>(null);

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/admin/software-updates']);
      return;
    }
    this.fetchUpdate(id);
  }

  private fetchUpdate(id: string): void {
    this.isLoading.set(true);
    this.service.getById(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.update.set(response.data);
        this.isLoading.set(false);
        this.refreshAssignedCount(id);
        // Covers hard refresh / direct links, where the list never named it.
        this.breadcrumbs.setLabel('id', response.data.id, `${response.data.name} ${response.data.version}`);
      },
      error: () => {
        this.toastService.show('Failed to load software update', 'error');
        this.isLoading.set(false);
        this.router.navigate(['/admin/software-updates']);
      },
    });
  }

  private refreshAssignedCount(id: string): void {
    this.service.getRolloutSummary(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => this.assignedCount.set(res.data.assignedCount),
      // Non-critical for the tab badge — fail quietly, RolloutTabComponent
      // surfaces its own error toast if the tab is actually opened.
      error: () => {},
    });
  }

  selectTab(tab: DetailTab): void {
    this.activeTab.set(tab);
  }

  /** A batch was submitted from the Assign devices tab — jump to Rollout,
   *  the standing answer to "where is this update across the fleet?", and
   *  refresh the badge since it just changed. */
  onAssigned(): void {
    this.activeTab.set('rollout');
    const id = this.update()?.id;
    if (id) this.refreshAssignedCount(id);
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }

  truncateChecksum(checksum: string | null): string {
    if (!checksum) return '—';
    return checksum.length > 8 ? `${checksum.slice(0, 8)}…${checksum.slice(-2)}` : checksum;
  }
}
